
// Import react elements
import React, { useEffect, useMemo, useState } from "react";

// Import hooks
import useCustomProductsData from "../../hooks/useCustomProductsData";
import { usePersistentFilters } from 'global/hooks/usePersistentFilters';
import { useToast } from "global/hooks/useToast";

// Import components
import CustomisableTable from 'global/components/CustomisableTable';
import CreateCheckboxFilters from 'global/components/CreateCheckboxFilters';
import CreateButton from "global/components/CreateButton";

// Import utility functions
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";



// Filter keys shared by the custom products table and grid
const CUSTOM_PRODUCT_FILTER_KEYS = ["category", "sub_category"];


// Table / Grid view of all custom products
const CustomProductsTable = ({
        setSelectedCustomProduct,
        setAddingNewCustomProduct,
        currentPage,
        setCurrentPage,
        rowsPerPage,
        setRowsPerPage,
        customProductsScrollPosition,
        shouldRestoreCustomProductsScrollPosition,
        rememberCustomProductsScrollPosition,
        onCustomProductsScrollPositionRestored,
        refreshKey,
    }) => {

    // Define toast options
    const { toastError } = useToast();

    // States for storing list of custom products
    const [customProducts, setCustomPrducts] = useState(null);

    // Store view grid type (grid or table) - true = grid, flase = table
    const [viewGridType, setViewGridType] = useState(false);

    // Loading state for fetching custom products
    const [loadingCustomProducts, setLoadingCustomProducts] = useState(false);

    // Set boolean state for displaying quick checkbox filters
    const [toggleTableQuickFilters, setToggleQuickFilters] = useState(true);
    
    // State for storing selected Link row in links table
    const [selectedCustomProductsRows, setSelectedCustomProductsRows] = useState([]);


    // State for storing custom products by catergory and subcategory


    // Custom hook for fetching and managing pod design and product data
    const {
        fetchCustomProducts,
        fetchCustomProduct,
        removeCustomProduct
    } = useCustomProductsData(); 



    // Fetch Custom Products on page load and whenever the editor closes
    useEffect(() => {

        fetchCustomProductsList();

    // Refresh key is deliberately controlled by the parent popup
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshKey]);


    
        
    // Function for fetching custom products
    const fetchCustomProductsList = async () => {

        // Set loading state
        setLoadingCustomProducts(true);

        try {

            // Make the call
            const customProductsList = await fetchCustomProducts();

            // Edit the data if we need to

            // Set links to above
            setCustomPrducts(customProductsList);

        } catch {

        } finally {
                
            // Clear loading state
            setLoadingCustomProducts(false);

        }

    };


        // Restore the custom products list position after its data has rendered
    useEffect(() => {

        // Do nothing unless a previous list position is waiting to be restored
        if (!shouldRestoreCustomProductsScrollPosition) {
            return;
        }

        // Wait until the custom product list has finished loading
        if (loadingCustomProducts) {
            return;
        }

        // Wait until the custom product rows are available
        if (!Array.isArray(customProducts)) {
            return;
        }

        // Store both animation frame ids for cleanup
        let firstAnimationFrameId = 0;
        let secondAnimationFrameId = 0;

        // Wait one frame for the table component to render
        firstAnimationFrameId = window.requestAnimationFrame(() => {

            // Wait another frame for pagination and row layout to settle
            secondAnimationFrameId = window.requestAnimationFrame(() => {

                // Normalise the stored position
                let restoredScrollPosition =
                    Number(customProductsScrollPosition);

                // Fall back to the top for an invalid stored position
                if (
                    !Number.isFinite(restoredScrollPosition) ||
                    restoredScrollPosition < 0
                ) {

                    restoredScrollPosition = 0;

                }

                // Restore the actual document scroll position
                if (document.scrollingElement) {

                    document.scrollingElement.scrollTop =
                        restoredScrollPosition;

                } else {

                    window.scrollTo(0, restoredScrollPosition);

                }

                // Tell the parent that restoration is complete
                if (
                    typeof onCustomProductsScrollPositionRestored ===
                    "function"
                ) {

                    onCustomProductsScrollPositionRestored();

                }

            });

        });

        // Cancel pending restoration if the list unmounts again
        return () => {

            window.cancelAnimationFrame(firstAnimationFrameId);
            window.cancelAnimationFrame(secondAnimationFrameId);

        };

    }, [
        customProducts,
        customProductsScrollPosition,
        loadingCustomProducts,
        onCustomProductsScrollPositionRestored,
        shouldRestoreCustomProductsScrollPosition,
    ]);

    // ******** Custom Products Table  ********
 
    // Columns for the links table
    const initialTableColumns = [
        // Select tick box at left of table rows - if we want to multi select orders and action them
        //{ key: 'select', label: '', resizable: false, minWidth: 40, maxWidth: 40, draggable: false, textFilter: false },
        //{ key: 'view', label: '', type: "text", resizable: false, minWidth: 50, maxWidth: 50, draggable: false, textFilter: false },   
        { key: 'custom_product_thumbnail_url', label: '', type: "image", imageNameKey: "name",  resizable: true, minWidth: 150, maxWidth: 500, draggable: false, textFilter: false },
        { key: 'name', label: 'Name', type: "text", resizable: true, minWidth: 150, maxWidth: 500, draggable: false, textFilter: true },
        { key: 'code', label: 'Code', type: "text", resizable: true, minWidth: 100, maxWidth: 500, draggable: false, textFilter: true },
        { key: 'stock_code', label: 'Stock Code', type: "text", resizable: true, minWidth: 100, maxWidth: 500, draggable: false, textFilter: true },
        { key: 'stock_used', label: 'Stock Used', type: "text", resizable: true, minWidth: 100, maxWidth: 200, draggable: false, textFilter: true },
        { key: 'brand', label: 'Brand', type: "text", resizable: true, minWidth: 100, maxWidth: 500, draggable: false, textFilter: true },
        { key: 'category', label: 'Category', type: "text", resizable: true, minWidth: 100, maxWidth: 500, draggable: false, textFilter: true },
        { key: 'sub_category', label: 'Sub Category', type: "text", resizable: true, minWidth: 100, maxWidth: 500, draggable: false, textFilter: true },
    ];


    // Set filters state - needs passing to filters component and table component
    const [filters, setFilters] = useState({});


    // This object maps internal filter keys to user-friendly labels
    const filterLabels = {
        category: 'Category',
        sub_category: 'Sub Category',
    };
 
 
    // useMemo to extract unique values from links
    const uniqueFilterOptions = useMemo(() => {

        // Create an object to hold options for each filter type
        const options = {};

        // Iterate through all rows of links
        customProducts?.forEach((row) => {

            ['category', 'sub_category'].forEach((key) => {

                // Initialize a Set for each key if it doesn't exist
                if (!options[key]) options[key] = new Set();

                // Add the value to the set to avoid duplicates
                if (row[key] !== undefined) options[key].add(row[key]);

            });

        });

        // Convert the sets into sorted arrays
        return Object.fromEntries(

            Object.entries(options).map(([key, set]) => [key, Array.from(set).sort()])

        );
 
        // Dependencies that trigger a new instance of this function
    }, [customProducts]);
 
 
    // Extract form-related state and functions from the `usePersistentFilters` custom hook  (for orders table filters)
    const {
        selectedFilters,
        toggleFilterValue,
        //toggleSelectAllFilters,
        //isAllSelected,
        clearFilterGroup,
    } = usePersistentFilters(
        "CustomProductsTable",
        CUSTOM_PRODUCT_FILTER_KEYS
    );
           
 
 
    // Update the table filters whenever the persistent quick filters change
    useEffect(() => {

        // Preserve any filters managed directly by the table
        setFilters((previousFilters) => {

            // Copy the existing table filters
            const nextFilters = {
                ...previousFilters,
            };

            // Remove the quick-filter groups before rebuilding them
            CUSTOM_PRODUCT_FILTER_KEYS.forEach((filterKey) => {

                delete nextFilters[filterKey];

            });

            // Add back only quick-filter groups which contain selected values
            Object.entries(selectedFilters || {}).forEach(([filterKey, selectedValues]) => {

                // Ignore empty filter groups because they mean no filtering
                if (!Array.isArray(selectedValues) || selectedValues.length < 1) {
                    return;
                }

                // Apply the active quick-filter values
                nextFilters[filterKey] = selectedValues;

            });

            // Return the cleaned table filters
            return nextFilters;

        });
 
    }, [selectedFilters]);
 
 

    // Check if any checkbox filters have been selected - so we can change the collapse/expand icon colour
    const anyFiltersSelected = Object.values(selectedFilters).some((selectedFilter) => selectedFilter.length > 0);


    // Function for click on a link row in the table (view link details)
    const handleSelectCustomProduct = async (customProductIds) => {

        // Remember exactly where the user was in the product list
        if (typeof rememberCustomProductsScrollPosition === "function") {

            rememberCustomProductsScrollPosition();

        }

        // Clear the 'adding new' flag
        setAddingNewCustomProduct(false);

        // Check if it's an array of ids, if so take the first one, else use it as is
        const customProductId = Array.isArray(customProductIds) ? customProductIds[0] : customProductIds;

        // Find the matching custom product in array with same id
        const customProductDetails = customProducts.find((product) => product.id === customProductId);

        // Fetch the custom product
        const response = await fetchCustomProduct(customProductDetails.code);

        // Update selected custom product state
        setSelectedCustomProduct(response || null);

    };
    

    // Create a memoized, descending-by-id copy of custom products - to send to the table/grid so the most recently created is at the top of the table
    const customProductsByOrderingDesc = useMemo(() => {

        // Guard: ensure we always work with an array
        const arr = Array.isArray(customProducts) ? customProducts : [];

        // Return a new array (don’t mutate state) sorted by numeric ordering descending
        return [...arr].sort((a, b) => Number(b?.ordering ?? 0) - Number(a?.ordering ?? 0));

        // Recompute whenever links reference changes
    }, [customProducts]);

    // Apply the persistent checkbox filters to the custom product grid
    const filteredCustomProductsByOrderingDesc = useMemo(() => {

        // Read only filter groups which currently contain selected values
        const filterEntries = Object.entries(selectedFilters || {}).filter((filterEntry) => {

            // Read the selected values from this filter group
            const selectedValues = filterEntry[1];

            // Empty groups mean that this field is not currently filtered
            return Array.isArray(selectedValues) && selectedValues.length > 0;

        });

        // Return every product when no active filter groups remain
        if (filterEntries.length < 1) {
            return customProductsByOrderingDesc;
        }

        // Apply every active filter group to the sorted products
        return customProductsByOrderingDesc.filter((product) => {

            // A product must match every filter group currently present
            return filterEntries.every(([key, values]) => {

                // Check whether this product value is selected
                return values.includes(product[key]);

            });

        });

    }, [customProductsByOrderingDesc, selectedFilters]);

    // Function to handle clicking 'add new custom product'
    const handleAddCustomProduct = () => {

        // set toggle stage for adding new?
        setAddingNewCustomProduct(true);

        // Set selected product to "new"?
        setSelectedCustomProduct({name:""});
        

    };


    // Function to handle delete click in table row menu
    const handleDeleteCustomProduct = (id) => {

        // Make sure customProducts is always an array
        const safeProducts = Array.isArray(customProducts) ? customProducts : [];

        // Find the product by id, normalising both to strings to avoid "1" vs 1 issues
        const productToDelete = safeProducts.find((product) => {

            // Return true when the ids match
            return String(product.id) === String(id);

        });

        // Display a confrimation dialog
        DisplayConfirmationDialog(

            // Dialog message
            "Are you sure you want to delete this custom product: " + productToDelete.name + "?",

            // Confirm button title
            "Delete",

            // Cancel button title
            "Cancel",

            // Returned response from the dialog box
            async function (is_confirmed) {
            
                // If they confirm
                if (is_confirmed) {

                    // Make the call
                    const response = await removeCustomProduct(productToDelete.code);

                    // If successful
                    if (response?.success) {

                        // Re fetch the list of custom products
                        fetchCustomProductsList();
                    
                    } else {

                        // Toast fail message
                        toastError({ title: "Failed", message: "Could not delete custom product: " + response.error_message, autoCloseMs: 6000 });

                    }
                        
                };

            }
            
        );

    };



    // Create menu options array - used for dropdown above table, per table row and within pop up?
    const customProductActionsConfig = [
        {
            id: "view", // Unique id
            label: 'View Details', // Title label
            clickFunction: handleSelectCustomProduct, // Click function used for table row button
            icon: '✏️', // Optional icon
            disabled: false, // Disabled state
            singleItemAction: true, // Is this action just for single item
        },
        {
            id: "delete",
            label: 'Delete',
            clickFunction: handleDeleteCustomProduct,
            icon: '🗑️',

            // Disable the delete button if this is a canonical customizr product
            disabled: (row) => String(row?.id).startsWith("10000"),
            color: 'red',
        },

    ];


     // Render the quick filters shared by the table and grid views
    const renderQuickFilters = () => {

        // Set the collapsed filter button defaults
        let filterButtonTitle = "Show Quick Filters";
        let filterButtonIcon = "🝖";
        let filterButtonClassName = "collapse_quick_filters_button";
        let filterGroups = "";

        // Show the filter controls when expanded
        if (toggleTableQuickFilters) {

            filterButtonTitle = "Hide Quick Filters";
            filterButtonIcon = "❮";

            filterGroups = (

                <div className="filter_groups">

                    <CreateCheckboxFilters
                        uniqueFilterOptions={uniqueFilterOptions}
                        selectedFilters={selectedFilters}
                        filterLabels={filterLabels}
                        clearFilterGroup={clearFilterGroup}
                        toggleFilterValue={toggleFilterValue}
                        setSelectedRows={setSelectedCustomProductsRows}
                    />

                </div>

            );

        }

        // Highlight the button while any filters are selected
        if (anyFiltersSelected) {
            filterButtonClassName += " highlight_quick_filters_icon";
        }

        // Return the complete filter sidebar
        return (

            <div className="collapsable_quick_filters">

                <div
                    className="collapse_quick_filters_button_banner"
                    onClick={() => {
                        setToggleQuickFilters((previousValue) => !previousValue);
                    }}
                >

                    <button
                        type="button"
                        title={filterButtonTitle}
                        className={filterButtonClassName}
                    >
                        {filterButtonIcon}
                    </button>

                </div>

                {filterGroups}

            </div>

        );

    };




/*
    // Set state for collapsing departments
    const [collapsedDepartments, setCollapsedDepartments] = useState({});

    // Function to toggle the department collapse / expand
    const handleToggleCollapse = (department) => {

        // Switch boolean based on previous state for specific department
        setCollapsedDepartments((prev) => ({

            ...prev,

            [department]: !prev[department],

        }));

    };

*/










    return (

        <>

    
            <div className="horizontal-align-content-center flex-justify-left-right">

                <div>
                    <h3 className="MMM-Grid_table-title">Custom Products ( {loadingCustomProducts ? "" : customProducts?.length} )</h3>
                </div>

                <div className="horizontal-align-content-center">
                
                    {/* This is the view mode toggle */}
                    <div className="pod-designs-page__view-toggle pod-pricing-page__section-tax-toggle">
                        <button
                            type="button"
                            className={`pod-pricing-page__segmented-button ${!viewGridType ? "pod-pricing-page__segmented-button--active" : ""}`}
                            onClick={() => setViewGridType(false)}
                        >
                            Table
                        </button>
                        <button
                            type="button"
                            className={`pod-pricing-page__segmented-button ${viewGridType ? "pod-pricing-page__segmented-button--active" : ""}`}
                            onClick={() => setViewGridType(true)}
                        >
                            Grid
                        </button>
                    </div>

                    <CreateButton title="+ Add Custom Product" size="l" onClick={() => {handleAddCustomProduct()}} />

                </div>

            </div>

            <br />

            {/* Check loading state */}
            {loadingCustomProducts &&
            
                <p>Loading Custom Products...</p>
                          
            }

            {!loadingCustomProducts && !viewGridType && customProducts && 

                <>

                    <div style={{ overflowX: 'auto', minWidth: '100%' }}>

                        <div className="quick_filters_and_table">

                            {renderQuickFilters()}

                            <div>

                                <CustomisableTable 
                                    tableData={customProductsByOrderingDesc}  // or customProducts
                                    initialTableColumns={initialTableColumns}   
                                    handleRowSingleClick={handleSelectCustomProduct}
                                    selectedRows={selectedCustomProductsRows}
                                    setSelectedRows={setSelectedCustomProductsRows}
                                    noDataAvaiableMessage={"No links have been created yet."}
                                    loadingData={loadingCustomProducts}
                                    filters={filters}
                                    setFilters={setFilters}
                                    currentPage={currentPage}
                                    setCurrentPage={setCurrentPage}
                                    rowsPerPage={rowsPerPage}
                                    setRowsPerPage={setRowsPerPage}
                                    menuOptions={customProductActionsConfig}
                                />

                            </div>

                        </div>

                    </div>

                </>

            }


            {!loadingCustomProducts && viewGridType && customProducts && 

                <div style={{ overflowX: "auto", minWidth: "100%" }}>

                    <div className="quick_filters_and_table">

                        {renderQuickFilters()}

                        <div className="custom-products-grid">

                    { customProducts?.length > 0 ? 

                        // Map through the custom products and display in image grid (add category/sub cat grouping or filtering)
                        filteredCustomProductsByOrderingDesc.map((prodData) => {

                            return (
                                                            
                                <div className="custom-products-grid" key={prodData.id} onClick={() => {handleSelectCustomProduct(prodData.id)}}>
                                        
                                    <div className="productCard productCard--sml">
                                        
                                        {prodData.custom_product_thumbnail_url ?
                                        
                                            // If there's a thumbnail image then show it
                                            <img src={prodData.custom_product_thumbnail_url} alt={prodData.name} />

                                            :

                                            // Otherwise show placeholder image
                                            <img src="/images/product_image_placeholder.png" alt={prodData.name} style={{opacity: "0.5"}}/>

                                        }
                                      
                                        <h3>{prodData.name}</h3>

                                        <p>{prodData.category} - {prodData.sub_category}</p>
                         
                                    </div>

                                </div>
                            
                            );

                        })

                        : 

                        "No custom products yet."
                            
                    }

                        </div>

                    </div>

                </div>
            }

        </>

    );

};

export default CustomProductsTable;